import { memo } from 'react';

import { PublicBoardNullState } from '~/components/PublicBoard/PublicBoardNullState';
import { usePublicBoardGalleryNestedControls } from '~/components/v2/components/board/gallery/usePublicBoardGalleryNestedControls';
import { type PublicBoardItemsViewProps } from '~/components/v2/components/board/PublicBoardContent';
import { GalleryView } from '~/components/v2/components/shared/gallery/GalleryView';
import { usePublicPermissionedVisibleCustomFieldsLength } from '~/components/v2/hooks/usePublicPermissionedVisibleCustomFieldsLength';
import { useGroupedGallerySelectedItems } from '~/hooks/useGroupedGallerySelectedItems';
import { usePublicShowcaseContext } from '~/providers/PublicShowcaseProvider';

export type PublicShowcaseBoardGalleryViewProps = PublicBoardItemsViewProps;

/**
 * Gallery of a board drilled inside a showcase. Cards and fetch come from the layout contract —
 * live `/a` ShortUrl children, Edit + Preview workspace children.
 */
export const PublicShowcaseBoardGalleryView = memo(({ scrollElementRef }: PublicShowcaseBoardGalleryViewProps) => {
  const { renderAsset, renderBoard, renderFile, useBoardGalleryViewItems } = usePublicShowcaseContext();
  const itemsData = useBoardGalleryViewItems();
  const { emptyStateHeader, renderHeader } = usePublicBoardGalleryNestedControls();

  return (
    <GalleryView
      itemsData={itemsData}
      // The BOARD null state, not the collection's: an empty board is an empty board wherever it
      // was drilled into from, and this one also handles the searching case. The collection's own
      // null state ("No content yet") belongs to the page body, not to a board inside it.
      emptyStateHeader={emptyStateHeader}
      NullState={PublicBoardNullState}
      renderAsset={renderAsset}
      renderBoard={renderBoard}
      renderFile={renderFile}
      renderHeader={renderHeader}
      scrollElementRef={scrollElementRef}
      useCustomFieldsLength={usePublicPermissionedVisibleCustomFieldsLength}
      useSelectableItems={useGroupedGallerySelectedItems}
    />
  );
});

PublicShowcaseBoardGalleryView.displayName = 'PublicShowcaseBoardGalleryView';
